import { Alert, AlertTitle, Box, Button, Chip, Paper, Stack, Typography } from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { ApiError } from '../../@types';
import { ResolveOpenBettingSlipsResponse, SuperAdminActiveTemplateResponse } from '../../@types/api';
import { QUERY_KEYS } from '../../constants';
import API_ROUTES from '../../constants/apiRoutes';
import { useModal } from '../../contexts/ModalContext';
import { useToast } from '../../contexts/ToastContext';
import { useToastErrorMessage } from '../../hooks/useToastErrorMessage';
import { getData, postData } from '../../utils/api';
import { mergeSx } from '../../utils/styles';
import ConfirmationModal from '../modals/ConfirmationModal';
import { useInvalidateQuery } from '../../hooks/useInvalidateQuery';

const styles = {
  page: {
    width: '100%',
    maxWidth: 960,
  },
  card: {
    p: { xs: 2, md: 3 },
    borderRadius: 2,
  },
  section: {
    gap: 3,
  },
  actionItem: {
    gap: 2,
    '&:not(:last-of-type)': {
      pb: 3,
      borderBottom: '1px solid',
      borderColor: 'divider',
    },
  },
  cardLayout: {
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) auto' },
    gap: { xs: 2, md: 3 },
    alignItems: { xs: 'flex-start', md: 'center' },
  },
  content: {
    minWidth: 0,
  },
  title: {
    fontWeight: 600,
  },
  actionButton: {
    minWidth: 112,
    justifySelf: { md: 'flex-end' },
  },
  resultChips: {
    flexWrap: 'wrap',
  },
  secondaryAction: {
    alignSelf: { xs: 'stretch', md: 'center' },
    width: { xs: '100%', md: 340 },
  },
  templateSelector: {
    p: 0.5,
    borderRadius: 999,
    backgroundColor: 'rgba(15, 23, 42, 0.06)',
  },
  templateOptionRow: {
    width: '100%',
    gap: 0.5,
    flexDirection: 'row',
  },
  templateOption: {
    flex: 1,
    minHeight: 44,
    borderRadius: 999,
    textTransform: 'none',
    fontWeight: 600,
    px: 1.25,
    justifyContent: 'center',
  },
  templateOptionActive: {
    backgroundColor: 'primary.main',
    color: 'primary.contrastText',
    boxShadow: '0 8px 20px rgba(15, 23, 42, 0.16)',
    '&:hover': {
      backgroundColor: 'primary.main',
    },
  },
  templateOptionInactive: {
    borderColor: 'transparent',
    color: 'text.secondary',
    backgroundColor: 'transparent',
    '&:hover': {
      borderColor: 'transparent',
      backgroundColor: 'rgba(255, 255, 255, 0.42)',
    },
  },
  templateOptionContent: {
    alignItems: 'center',
    justifyContent: 'center',
    gap: 0.75,
  },
  templateOptionLabel: {
    fontWeight: 700,
  },
  templateOptionBadge: {
    px: 0.75,
    py: 0.25,
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 700,
    lineHeight: 1,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
  },
  templateOptionBadgeActive: {
    backgroundColor: 'rgba(255, 255, 255, 0.18)',
    color: 'common.white',
  },
};

type TemplateTarget = 'main' | 'backup';

const SuperAdminActionsPage = () => {
  const invalidateData = useInvalidateQuery();
  const { openModal, closeModal } = useModal();
  const { enqueueSnackbar } = useToast();
  const { handleErrorMessage } = useToastErrorMessage();
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateTarget | null>(null);

  const {
    data: activeTemplateData,
    isPending: isActiveTemplateLoading,
    isFetching: isActiveTemplateFetching,
    isError: isActiveTemplateError,
    error: activeTemplateError,
  } = useQuery<SuperAdminActiveTemplateResponse, ApiError>({
    queryKey: [QUERY_KEYS.superAdminActiveTemplate],
    queryFn: () => getData(API_ROUTES.BO_TEMPLATES_ACTIVE, undefined, 'ftbConsumer'),
  });

  useEffect(() => {
    if (activeTemplateData) {
      setSelectedTemplate(activeTemplateData.activeTemplate);
    }
  }, [activeTemplateData]);

  useEffect(() => {
    if (isActiveTemplateError && activeTemplateError) {
      handleErrorMessage(activeTemplateError);
    }
  }, [activeTemplateError, handleErrorMessage, isActiveTemplateError]);

  const invalidateActiveTemplate = () => {
    invalidateData([QUERY_KEYS.superAdminActiveTemplate]);
  };

  const resolveOpenBettingSlipsMutation = useMutation<ResolveOpenBettingSlipsResponse, ApiError, void>({
    mutationFn: () => {
      return postData(API_ROUTES.SUPER_ADMIN_RESOLVE_OPEN_BETTING_SLIPS, {}, 'ftbConsumer');
    },
    onSuccess: () => {
      enqueueSnackbar({ message: 'Open betting slip resolution finished successfully.', variant: 'success' });
    },
    onError: (error) => {
      handleErrorMessage(error);
    },
  });

  const requestSnapshotMutation = useMutation<void, ApiError, void>({
    mutationFn: () => {
      return postData(API_ROUTES.BO_TEMPLATES_REQUEST_SNAPSHOT, {}, 'ftbConsumer');
    },
    onSuccess: () => {
      enqueueSnackbar({
        message: 'Snapshot requested successfully.',
        variant: 'success',
      });
    },
    onError: (error) => {
      handleErrorMessage(error);
    },
  });

  const activateTemplateMutation = useMutation<void, ApiError, TemplateTarget>({
    mutationFn: (templateTarget) => {
      return postData(`${API_ROUTES.BO_TEMPLATES_ACTIVATE}/${templateTarget}`, {}, 'ftbConsumer');
    },
    onSuccess: (_, templateTarget) => {
      enqueueSnackbar({
        message: `${templateTarget === 'main' ? 'Main' : 'Backup'} template activated successfully.`,
        variant: 'success',
      });
      invalidateActiveTemplate();
    },
    onError: (error) => {
      handleErrorMessage(error);
    },
  });

  const openResolveConfirmationModal = () => {
    openModal(
      <ConfirmationModal
        title='Run open betting slip resolution now?'
        actionButtonLabel='Confirm'
        actionButtonClose='Cancel'
        handleConfirm={() => resolveOpenBettingSlipsMutation.mutate()}
        closeModal={closeModal}
      />
    );
  };

  const openSnapshotConfirmationModal = () => {
    openModal(
      <ConfirmationModal
        title='Request a snapshot now?'
        actionButtonLabel='Confirm'
        actionButtonClose='Cancel'
        handleConfirm={() => requestSnapshotMutation.mutate()}
        closeModal={closeModal}
      />
    );
  };

  const openTemplateActivationConfirmationModal = (templateTarget: TemplateTarget) => {
    openModal(
      <ConfirmationModal
        title={`Switch to ${templateTarget} template now?`}
        actionButtonLabel='Yes'
        actionButtonClose='Cancel'
        handleConfirm={() => activateTemplateMutation.mutate(templateTarget)}
        closeModal={closeModal}
      />
    );
  };

  const isTemplateInteractionDisabled =
    isActiveTemplateLoading || isActiveTemplateFetching || !selectedTemplate || activateTemplateMutation.isPending;

  return (
    <Stack sx={styles.page}>
      <Paper elevation={0} sx={styles.card}>
        <Stack sx={styles.section}>
          <Stack sx={styles.actionItem}>
            <Stack sx={styles.cardLayout}>
              <Stack spacing={0.75} sx={styles.content}>
                <Typography variant='h6' sx={styles.title}>
                  Resolve Open Betting Slips
                </Typography>
                <Typography
                  variant='body2'
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  Re-run the backend resolution flow for slips that are still open and need another pass.
                </Typography>
              </Stack>

              <Button
                variant='contained'
                onClick={openResolveConfirmationModal}
                disabled={resolveOpenBettingSlipsMutation.isPending}
                sx={styles.actionButton}
              >
                Run
              </Button>
            </Stack>

            {resolveOpenBettingSlipsMutation.data && (
              <Alert severity='success'>
                <AlertTitle>Last run summary</AlertTitle>
                <Stack direction='row' spacing={1} useFlexGap sx={styles.resultChips}>
                  <Chip
                    size='small'
                    label={`Open slips: ${resolveOpenBettingSlipsMutation.data.openBettingSlipsCount}`}
                  />
                  <Chip
                    size='small'
                    label={`Resolved: ${resolveOpenBettingSlipsMutation.data.resolvedBettingSlipsCount}`}
                  />
                  <Chip size='small' label={`Updated bets: ${resolveOpenBettingSlipsMutation.data.updatedBetsCount}`} />
                  <Chip
                    size='small'
                    label={`Failed: ${resolveOpenBettingSlipsMutation.data.failedBettingSlipsCount}`}
                  />
                </Stack>
              </Alert>
            )}
          </Stack>

          <Stack sx={styles.actionItem}>
            <Stack sx={styles.cardLayout}>
              <Stack spacing={0.75} sx={styles.content}>
                <Typography variant='h6' sx={styles.title}>
                  Request Snapshot
                </Typography>
                <Typography
                  variant='body2'
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  Trigger a fresh Feed The Bet snapshot request when you need the platform to reload current feed state.
                </Typography>
              </Stack>

              <Button
                variant='contained'
                onClick={openSnapshotConfirmationModal}
                disabled={requestSnapshotMutation.isPending}
                sx={styles.actionButton}
              >
                Request
              </Button>
            </Stack>
          </Stack>

          <Stack sx={styles.actionItem}>
            <Stack sx={styles.cardLayout}>
              <Stack spacing={0.75} sx={styles.content}>
                <Typography variant='h6' sx={styles.title}>
                  Switch Feed Template
                </Typography>
                <Typography
                  variant='body2'
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  After switching, check the RabbitMQ dashboard to confirm the fixtures queue has acknowledged all
                  messages. If the app is still in maintenance mode after that, turn the Feed Running flag back on in
                  the Flags tab.
                </Typography>
              </Stack>

              <Stack sx={mergeSx(styles.secondaryAction, styles.templateSelector)}>
                <Stack sx={styles.templateOptionRow}>
                  {(['main', 'backup'] as const).map((templateTarget) => {
                    const isActive = selectedTemplate === templateTarget;
                    const label = templateTarget === 'main' ? 'Main' : 'Backup';

                    return (
                      <Button
                        key={templateTarget}
                        variant={isActive ? 'contained' : 'outlined'}
                        color={isActive ? 'primary' : 'inherit'}
                        aria-label={label}
                        aria-pressed={isActive}
                        disabled={isTemplateInteractionDisabled}
                        onClick={() => {
                          if (isActive || isTemplateInteractionDisabled) {
                            return;
                          }

                          openTemplateActivationConfirmationModal(templateTarget);
                        }}
                        sx={mergeSx(
                          styles.templateOption,
                          isActive ? styles.templateOptionActive : styles.templateOptionInactive
                        )}
                      >
                        <Stack direction='row' sx={styles.templateOptionContent}>
                          <Typography variant='body2' sx={styles.templateOptionLabel}>
                            {label}
                          </Typography>
                          {isActive ? (
                            <Box
                              component='span'
                              aria-hidden='true'
                              sx={mergeSx(styles.templateOptionBadge, styles.templateOptionBadgeActive)}
                            >
                              Active
                            </Box>
                          ) : null}
                        </Stack>
                      </Button>
                    );
                  })}
                </Stack>
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      </Paper>
    </Stack>
  );
};

export default SuperAdminActionsPage;
